<template>
  <div class="icon-menu-container">
    <a href="#"
      class="menu-trigger"
      ref="trigger"
      :class="{ active: isOpen }"
      @click.stop.prevent="toggleMenu">
      <i :class="icon"></i>
    </a>

    <transition name="menu-slide">
      <div v-if="isOpen"
        ref="menu"
        class="vertical-menu"
        :class="placementClass">
        <a href="#" v-for="item in items"
          class="vertical-menu-item"
          :key="item.id"
          :title="item.label"
          @click.stop.prevent="selectItem(item)">
          <span class="menu-icon">
            <i :class="item.icon"></i>
          </span>
          <span class="menu-label">
            {{ item.label }}
          </span>
        </a>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from "vue";

const menuBus = new Vue();

export default {
  name: "VerticalIconMenu",

  props: {
    icon: {
      type: String,
      default: "bi bi-list",
    },
    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isOpen: false,
      placement: "bottom-right",
      menuId: `vertical-icon-menu-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    };
  },

  computed: {
    placementClass() {
      return `placement-${this.placement}`;
    },
  },

  mounted() {
    this.updatePlacement();

    window.addEventListener("resize", this.updatePlacement);
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("mouseover", this.onDocumentMouseOver);

    menuBus.$on("vertical-icon-menu:open", this.handleMenuOpened);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updatePlacement);
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("mouseover", this.onDocumentMouseOver);

    menuBus.$off("vertical-icon-menu:open", this.handleMenuOpened);
  },

  methods: {
    handleMenuOpened(id) {
      if (id !== this.menuId) {
        this.isOpen = false;
      }
    },

    onDocumentClick(event) {
      const target = event.target;

      if (this.isOpen && !this.$el.contains(target)) {
        this.isOpen = false;
      }
    },

    onDocumentMouseOver(event) {
      if (!this.isOpen) return;

      const target = event.target;

      if (this.$el.contains(target)) {
        return;
      }

      const rect = this.$el.getBoundingClientRect();
      const buffer = 14;

      const insideBuffer =
        event.clientX >= rect.left - buffer &&
        event.clientX <= rect.right + buffer &&
        event.clientY >= rect.top - buffer &&
        event.clientY <= rect.bottom + buffer;

      if (insideBuffer) {
        return;
      }

      const currentRow = this.$el.closest("tr");
      const hoveredRow = target && target.closest ? target.closest("tr") : null;

      if (hoveredRow && currentRow && hoveredRow !== currentRow) {
        this.isOpen = false;
        return;
      }

      this.isOpen = false;
    },

    toggleMenu(event) {
      event?.stopPropagation();

      this.updatePlacement();

      if (this.isOpen) {
        this.isOpen = false;
        return;
      }

      menuBus.$emit("vertical-icon-menu:open", this.menuId);
      this.isOpen = true;
    },

    selectItem(item, event) {
      event?.stopPropagation();
      this.$emit("select", item);
      this.isOpen = false;
    },

    updatePlacement() {
      const trigger = this.$refs.trigger;
      const menu = this.$refs.menu;

      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const menuWidth = menu ? menu.offsetWidth : 200;
      const menuHeight = menu ? menu.offsetHeight : 220;

      const spaceRight = window.innerWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;
      const spaceBottom = window.innerHeight - triggerRect.bottom;
      const spaceTop = triggerRect.top;

      const vertical =
        spaceBottom >= menuHeight
          ? "bottom"
          : spaceTop >= menuHeight
            ? "top"
            : spaceBottom >= spaceTop
              ? "bottom"
              : "top";

      const horizontal =
        spaceRight >= menuWidth
          ? "right"
          : spaceLeft >= menuWidth
            ? "left"
            : spaceRight >= spaceLeft
              ? "right"
              : "left";

      this.placement = `${vertical}-${horizontal}`;
    },
  },
};
</script>

<style scoped>
.icon-menu-container {
  position: relative;
  display: inline-block;
  padding-bottom: 12px;
  margin-bottom: -12px;
}

.vertical-menu {
  position: absolute;
  z-index: 1050;
  min-width: 200px;
  padding: 6px 0;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.vertical-menu.placement-bottom-right {
  top: calc(100% + 5px);
  left: 0;
  transform: translateX(-20px);
}

.vertical-menu.placement-bottom-left {
  top: calc(100% + 5px);
  right: 0;
  left: auto;
  transform: translateX(20px);
}

.vertical-menu.placement-top-right {
  bottom: calc(100% + 5px);
  left: 0;
  transform: translateX(-20px);
}

.vertical-menu.placement-top-left {
  bottom: calc(100% + 5px);
  right: 0;
  left: auto;
  transform: translateX(20px);
}

.vertical-menu-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: #3c4b64;
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.vertical-menu-item:hover {
  color: #369;
  font-weight: bold;
  background-color: #f0f1f3;
  text-decoration: none;
}

.menu-icon {
  width: 30px;
  font-size: 17px;
}

.menu-label {
  font-size: 14px;
}

.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: all 0.2s ease;
  transform-origin: top;
}

.menu-slide-enter,
.menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
